import { Box, Card, Group, Table, Text, ThemeIcon } from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { formatNumber, numberLocale } from '@/lib/money';

type Cell = string | 'check' | 'x';

interface Row {
  key: string;
  formshive: Cell;
  wufoo: Cell;
  formspree: Cell;
}

function comparisonRows(t: TFunction, lang: string): Row[] {
  const usd = (n: number) =>
    new Intl.NumberFormat(numberLocale(lang), {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(n);
  const num = (n: number) => formatNumber(n, lang);
  const mb = (n: number) => `${num(n)} MB`;
  const perYear = (n: number) => t('billing.compare.perYear', { amount: usd(n) });
  const perMonth = (amount: string) => t('billing.compare.perMonth', { amount });
  const unlimited = t('billing.compare.unlimited');
  const unknown = t('billing.compare.unknown');
  const recaptcha = 'reCAPTCHA (Google)';

  return [
    { key: 'cost', formshive: perYear(210), wufoo: perYear(264), formspree: perYear(180) },
    { key: 'forms', formshive: unlimited, wufoo: num(10), formspree: num(5) },
    {
      key: 'entries',
      formshive: perMonth(num(2000)),
      wufoo: perMonth(num(1000)),
      formspree: perMonth(num(200)),
    },
    { key: 'users', formshive: unlimited, wufoo: num(1), formspree: num(1) },
    { key: 'reports', formshive: unlimited, wufoo: unlimited, formspree: unlimited },
    { key: 'fileStorage', formshive: perMonth(mb(500)), wufoo: '1 GB', formspree: '1 GB' },
    { key: 'uploadLimit', formshive: mb(100), wufoo: mb(25), formspree: mb(25) },
    {
      key: 'apiRequests',
      formshive: unlimited,
      wufoo: num(10000),
      formspree: t('billing.compare.unlimitedUnsure'),
    },
    { key: 'confirmationEmail', formshive: 'check', wufoo: 'check', formspree: 'check' },
    { key: 'redirects', formshive: 'check', wufoo: 'check', formspree: 'check' },
    { key: 'autoReply', formshive: 'check', wufoo: 'check', formspree: 'x' },
    {
      key: 'captcha',
      formshive: t('billing.compare.altcha'),
      wufoo: recaptcha,
      formspree: recaptcha,
    },
    {
      key: 'retention',
      formshive: t('billing.compare.freeTierDays', { days: 30 }),
      wufoo: unknown,
      formspree: unknown,
    },
  ];
}

const NAVY = '#0A192F';

function CellContent({ value, highlight }: { value: Cell; highlight?: boolean }) {
  const { t } = useTranslation();
  if (value === 'check' || value === 'x') {
    const yes = value === 'check';
    return (
      <Group gap={4} justify="center">
        <ThemeIcon
          size={18}
          radius="xl"
          variant="light"
          color={yes && highlight ? 'dark' : 'gray'}
          role="img"
          aria-label={t(yes ? 'billing.compare.yes' : 'billing.compare.no')}
        >
          {yes ? <IconCheck size={12} /> : <IconX size={12} />}
        </ThemeIcon>
      </Group>
    );
  }
  return (
    <Text size="sm" ta="center" fw={highlight ? 600 : 400} c={highlight ? undefined : 'dimmed'}>
      {value}
    </Text>
  );
}

export function Plans() {
  const { t, i18n } = useTranslation();
  const data = comparisonRows(t, i18n.language);

  return (
    <Card withBorder radius="md" p={0} style={{ overflow: 'hidden' }}>
      <Table verticalSpacing="md" horizontalSpacing="lg" withRowBorders={false} layout="fixed">
        <Table.Thead style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
          <Table.Tr>
            <Table.Th />
            <Table.Th>
              <Text size="sm" fw={700} ta="center" c={NAVY}>
                Formshive
              </Text>
            </Table.Th>
            <Table.Th>
              <Text size="sm" fw={600} ta="center" c="dimmed">
                Wufoo
              </Text>
            </Table.Th>
            <Table.Th>
              <Text size="sm" fw={600} ta="center" c="dimmed">
                Formspree
              </Text>
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {data.map((row, idx) => (
            <Table.Tr
              key={row.key}
              style={{
                borderTop: idx === 0 ? '1px solid var(--mantine-color-gray-2)' : undefined,
                borderBottom:
                  idx < data.length - 1 ? '1px solid var(--mantine-color-gray-2)' : undefined,
              }}
            >
              <Table.Td>
                <Text size="sm" fw={500}>
                  {t(`billing.compare.row.${row.key}`)}
                </Text>
              </Table.Td>
              <Table.Td style={{ backgroundColor: 'rgba(10, 25, 47, 0.03)' }}>
                <CellContent value={row.formshive} highlight />
              </Table.Td>
              <Table.Td>
                <CellContent value={row.wufoo} />
              </Table.Td>
              <Table.Td>
                <CellContent value={row.formspree} />
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      <Box px="lg" py="sm" style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }}>
        <Text size="xs" c="dimmed" ta="center">
          {t('billing.compare.footnote')}
        </Text>
      </Box>
    </Card>
  );
}
